import BaseIconSvg from '@/components/bases/BaseIconSvg';
import constants from '@/constants';
import emrTypeHelper from '@/helper/emrType';
import FamilyHistory from '@/screens/PHR/components/FamilyHistory';
import InfoSection from '@/screens/PHR/components/InfoSection';
import PersonalHistory from '@/screens/PHR/components/PersonalHistory';
import DetailContent from '@/screens/Timeline/components/DetailContent';
import useGlobalConfigs from '@/stores/globalConfigs';
import { usePhrStoreInContext } from '@/stores/phr';
import { useQuickModal } from '@/utils/quickModal';
import { Col, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as React from 'react';

export interface IPHRSidebarProps {
  setCollapsed: (_: boolean) => void;
  collapsed: boolean;
}

const PHRSidebar: React.FC<IPHRSidebarProps> = ({ collapsed, setCollapsed, ...props }) => {
  const [currentModal, setCurrentModal] = React.useState<any>(null);
  const [key, setKey] = React.useState<string>('');
  const { quickModal, quickModalContext } = useQuickModal();
  const components = usePhrStoreInContext((s) => s.phr?.components);
  const phrConfigs = useGlobalConfigs((s) => s.phrConfigs);
  const emrTypes = useGlobalConfigs((s) => s.emrTypes);
  const objComponent = components?.reduce((d: any, e: IEMRData) => {
    d[e.type.code] = e;
    return d;
  }, {});
  const currentKey = React.useRef<string>('');
  React.useEffect(() => {
    currentKey.current = key;
  }, [key]);
  return (
    <>
      {quickModalContext}
      <div
        className={`${collapsed ? 'w-[100px]' : 'w-[300px]'} duration-100 h-full rounded-lg p-2 flex-grow flex-none`}
      >
        <div className="bg-white rounded-lg h-full shadow flex flex-col">
          <div className="flex-grow">
            <InfoSection
              onChange={() => {
                Modal.destroyAll();
                setKey('');
              }}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
            />
          </div>
          <div className="border-t border-gray-3 h-full bg-white relative rounded-b-lg">
            <div className="absolute top-0 left-0 bottom-0 right-0 overflow-y-auto overflow-x-hidden">
              {phrConfigs?.sidebar?.map((group, i: number) => {
                const valueInGroup = group?.index
                  ?.map((d, j: number) => {
                    const data: any = {};
                    const componentData = objComponent?.[d.emrTypeGetData];
                    const emrConfig = emrTypes?.[d.emrTypeOpen];

                    Object.keys(d.routeValue || {})?.forEach((k) => {
                      const route: string = d.routeValue?.[k]?.value || d.routeValue?.[k];
                      const routeConfig = d.routeValue?.[k]?.config;

                      data[k] = _.get(componentData, route);

                      if (routeConfig) {
                        const [emrTypeCode, fieldKey] = routeConfig.split('.');
                        const emrType = emrTypes?.[emrTypeCode];
                        const fieldConfig: IFieldConfig = emrType?.format.fields.find(
                          (f) => f.key === fieldKey,
                        ) as IFieldConfig;
                        const valueOption = emrTypeHelper.getOption(fieldConfig, data[k]);
                        if (valueOption) {
                          data[k] = `<b style="color: ${valueOption.color}">
                          ${valueOption.label}
                        </b>`;
                        } else if (fieldConfig?.inputType === 'date') {
                          data[k] = data[k]
                            ? dayjs(data[k]).format(fieldConfig.extra.format || constants.DATE_FORMAT)
                            : data[k];
                        } else if (fieldConfig?.inputType === 'number' && data[k]) {
                          data[k] = emrTypeHelper.toFix(fieldConfig, data[k]);
                        }
                      }
                    });

                    const stringFormat = collapsed ? d?.formatShort : d?.format;

                    const show = stringFormat?.replace(/\{([\w.]+)\}/g, (match: any, key: string | number) => {
                      return typeof data?.[key] !== 'undefined' ? data?.[key] : '--';
                    });

                    if (d.noDataIsHidden && !componentData) {
                      return;
                    }

                    return (
                      <Col
                        key={j}
                        style={{ width: d.width && !collapsed ? `${d.width * 100}%` : '100%' }}
                        onDoubleClick={() => {
                          console.log({ key: d.emrTypeGetData, emrConfig, data: componentData });
                        }}
                      >
                        <div
                          onClick={() => {
                            if (currentModal) {
                              currentModal.destroy();
                            }

                            // Mở View xem
                            const result = quickModal?.custom({
                              afterClose() {
                                !result.open && setKey('');
                              },
                              mask: false,
                              title: d?.label || emrConfig?.name,
                              width: 900,

                              content: (() => {
                                switch (d?.key) {
                                  case 'anamnesis':
                                    return <PersonalHistory />;
                                  case 'family':
                                    return <FamilyHistory />;
                                  case 'riskFactor':
                                  case 'totalReact':
                                  default:
                                    return <DetailContent emr={componentData} emrType={emrConfig as IEMRType} />;
                                }
                              })(),
                              //   content: (
                              //     <Content emr={componentData} emrType={emrConfig as IEMRType} sidebarKey={d?.key} />
                              //   ),
                            });
                            setKey(d.key);
                            setCurrentModal(result);
                          }}
                          className={`hover:border-mint-3 gap-2 hover:bg-yellow-1 cursor-pointer py-1 flex items-center flex-none ${d.key === key ? 'bg-mint-1' : ''} ${collapsed ? 'justify-center flex-wrap text-center' : 'px-4'} empty:hidden`}
                        >
                          {d?.icon && <BaseIconSvg src={d?.icon || ''} className="wh-[24px]" />}
                          {collapsed ? (
                            show && (
                              <div
                                className="text-xs text-gray-7 w-full"
                                dangerouslySetInnerHTML={{ __html: show || '' }}
                              ></div>
                            )
                          ) : (
                            <div className="whitespace-nowrap overflow-hidden w-full">
                              {d.label} <div dangerouslySetInnerHTML={{ __html: show || '' }}></div>
                            </div>
                          )}
                        </div>
                      </Col>
                    );
                  })
                  .filter(Boolean);

                if (group.noDataIsHidden && !valueInGroup?.length) {
                  return null;
                }

                return (
                  <React.Fragment key={i.toString()}>
                    <div
                      className={`mb-2 text-sm font-bold border-t border-gray-3 pt-2 mt-2 empty:hidden  ${collapsed ? 'text-center' : 'px-2'}`}
                    >
                      {collapsed ? group.labelShort : group.label}
                    </div>
                    <Row gutter={[8, 8]} className="empty:hidden">
                      {valueInGroup}
                    </Row>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default PHRSidebar;
